import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { PrettoSlider } from '../Chat/components/CaptureAudio';
import { formatTime } from '../../utils/dateUtils';

function VoiceRecording({ message }: { message: string }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
  const [audioMessage, setAudioMessage] = useState<HTMLAudioElement | null>(null);
  const [totalDuration, setTotalDuration] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const { location } = window;
  const [wasPlayingBeforeDrag, setWasPlayingBeforeDrag] = useState(false);

  useEffect(() => {
    if (audioMessage) {
      const updatePlaybackTime = () => {
        setCurrentPlaybackTime(audioMessage.currentTime);
      };
      audioMessage.addEventListener('timeupdate', updatePlaybackTime);
      return () => {
        audioMessage.removeEventListener('timeupdate', updatePlaybackTime);
      };
    }
  }, [audioMessage]);

  useEffect(() => {
    const audioURL = `${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${message}`;
    const audio = new Audio(audioURL);
    setAudioMessage(audio);

    const setDuration = () => {
      setTotalDuration(audio.duration);
      setLoading(false);
    };

    audio.addEventListener('loadedmetadata', setDuration);

    return () => {
      audio.removeEventListener('loadedmetadata', setDuration);
    };
  }, [message]);

  // Pause the audio if the URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      if (audioMessage && !audioMessage.paused) {
        audioMessage.pause();
        setIsPlaying(false);
        audioMessage.currentTime = 0;
        setCurrentPlaybackTime(0);
      }
    };

    handleUrlChange();

    return () => {
      handleUrlChange();
    };
  }, [location, audioMessage]);

  const handlePlayAudio = () => {
    if (audioMessage) {
      audioMessage.play();
      setIsPlaying(true);
    }
  };

  const handlePauseAudio = () => {
    if (audioMessage) {
      audioMessage.pause();
      setIsPlaying(false);
    }
  };

  // Handle when the user starts dragging the slider
  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setCurrentPlaybackTime(newValue as number);
  };

  // Handle when the user stops dragging and sets the new time
  const handleSliderChangeCommitted = (
    event: React.SyntheticEvent | Event,
    newValue: number | number[]
  ) => {
    if (audioMessage) {
      // Seek the audio to the new time
      audioMessage.currentTime = newValue as number;
      setCurrentPlaybackTime(audioMessage.currentTime);

      // If the audio was playing before the drag, resume playing
      if (wasPlayingBeforeDrag) {
        audioMessage.play();
        setIsPlaying(true);
      }
    }
  };

  // Track if the audio was playing before dragging started
  const handleSliderDragStart = () => {
    if (isPlaying && audioMessage) {
      setWasPlayingBeforeDrag(true);
      audioMessage.pause();
    } else {
      setWasPlayingBeforeDrag(false);
    }
  };

  return (
    <>
      {audioMessage && !isPlaying && (
        <Grid display="flex" flexDirection="column">
          <Grid gap={2} display="flex" flexDirection="row" alignItems="center" width="100%">
            <Box onClick={handlePlayAudio} display="flex" alignItems="center">
              <img
                src="/assets/icons/play-circle.svg"
                alt="start-playing"
                style={{ cursor: 'pointer' }}
              />
            </Box>
            <Box width="calc(100% - 60px)" display="flex" flexDirection="column" gap="10px">
              <PrettoSlider
                valueLabelDisplay="auto"
                aria-label="pretto slider"
                min={0}
                max={totalDuration}
                value={currentPlaybackTime}
                onChange={handleSliderChange}
                onChangeCommitted={handleSliderChangeCommitted}
                onMouseDown={handleSliderDragStart}
                isplaying
              />
            </Box>
          </Grid>
          <Grid display="flex" flexDirection="row" justifyContent="space-between" pr={1.5} pl={6}>
            <Typography variant="tinyTexts" color="primary">
              {formatTime(currentPlaybackTime)}
            </Typography>
            {loading ? (
              <CircularProgress size={12} />
            ) : (
              <Typography variant="tinyTexts" color="primary">
                {formatTime(totalDuration)}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
      {audioMessage && isPlaying && (
        <Grid display="flex" flexDirection="column">
          <Grid gap={2} display="flex" flexDirection="row" alignItems="center" width="100%">
            {formatTime(currentPlaybackTime) !== formatTime(totalDuration) ? (
              <Box display="flex" alignItems="center" onClick={handlePauseAudio}>
                <img
                  src="/assets/icons/pause-circle.svg"
                  alt="pause-playing"
                  style={{ cursor: 'pointer' }}
                />
              </Box>
            ) : (
              <Box onClick={handlePlayAudio} display="flex" alignItems="center">
                <img
                  src="/assets/icons/play-circle.svg"
                  alt="start-playing"
                  style={{ cursor: 'pointer' }}
                />
              </Box>
            )}

            <Box width="calc(100% - 60px)" display="flex" flexDirection="column" gap="10px">
              <PrettoSlider
                valueLabelDisplay="auto"
                aria-label="pretto slider"
                min={0}
                max={totalDuration}
                value={currentPlaybackTime}
                onChange={handleSliderChange}
                onChangeCommitted={handleSliderChangeCommitted}
                onMouseDown={handleSliderDragStart}
                isplaying
              />
            </Box>
          </Grid>
          <Grid display="flex" flexDirection="row" justifyContent="space-between" pr={1.5} pl={6}>
            <Typography variant="tinyTexts" color="primary">
              {formatTime(currentPlaybackTime)}
            </Typography>
            {loading ? (
              <CircularProgress size={12} />
            ) : (
              <Typography variant="tinyTexts" color="primary">
                {formatTime(totalDuration)}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
}

export default VoiceRecording;
